'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, Loader2, AlertCircle, ChevronRight, ChevronLeft, Check, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

interface SignUpFormProps {
  onSwitchToLogin: () => void;
}

interface Step1Data {
  fullName: string;
  email: string;
  password: string;
  confirmPassword: string;
}

interface Step2Data {
  businessName: string;
  phone1: string;
  phone2: string;
  website: string;
  businessCategory: string;
  country: string;
}

interface Step3Data {
  primaryColor: string;
  defaultLanguage: string;
}

interface Step4Data {
  plan: string;
}

const BUSINESS_CATEGORIES = [
  'Fashion & Apparel', 'Electronics', 'Food & Beverage', 'Beauty & Skincare',
  'Jewellery', 'Home Decor', 'Fitness & Wellness', 'Education', 'Real Estate',
  'Automobile', 'Healthcare', 'Travel & Hospitality', 'Other',
];

const PLANS = [
  { id: 'free', name: 'FREE', price: '₹0/mo', creatives: '3 AI ads', highlight: false },
  { id: 'starter', name: 'STARTER', price: '₹799/mo', creatives: '30 ad creatives', highlight: false },
  { id: 'pro', name: 'PRO', price: '₹1,299/mo', creatives: '100 ad creatives', highlight: true },
  { id: 'business', name: 'BUSINESS', price: '₹1,999/mo', creatives: 'High usage limits', highlight: false },
];

const STEP_LABELS = ['Account', 'Brand', 'Style', 'Plan'];

const INDIAN_PHONE_REGEX = /^(\+91[\s-]?)?[6-9]\d{4}[\s]?\d{5}$|^[6-9]\d{9}$/;

export default function SignUpForm({ onSwitchToLogin }: SignUpFormProps) {
  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('pro');
  const { signInWithGoogle } = useAuth();

  const step1Form = useForm<Step1Data>();
  const step2Form = useForm<Step2Data>({ defaultValues: { country: 'India', businessCategory: '' } });
  const step3Form = useForm<Step3Data>({ defaultValues: { primaryColor: '#7C3AED', defaultLanguage: 'English' } });

  const handleStep1 = step1Form.handleSubmit(() => setStep(2));
  const handleStep2 = step2Form.handleSubmit(() => setStep(3));
  const handleStep3 = step3Form.handleSubmit(() => setStep(4));

  const handleFinalSubmit = async () => {
    setIsLoading(true);
    // Backend integration point: POST /api/auth/register with all step data + selectedPlan
    await new Promise((r) => setTimeout(r, 2000));
    toast.success('Account created! Setting up your brand workspace...');
    setTimeout(() => {
      window.location.href = '/my-creatives';
    }, 1500);
  };

  const handleGoogleSignUp = async () => {
    setIsGoogleLoading(true);
    try {
      await signInWithGoogle();
      // Supabase will redirect to /auth/callback automatically
    } catch {
      setIsGoogleLoading(false);
    }
  };

  const s1 = step1Form.formState.errors;
  const s2 = step2Form.formState.errors;

  return (
    <div className="animate-slide-up">
      {/* Step indicator */}
      <div className="flex items-center gap-0 mb-8">
        {STEP_LABELS.map((label, i) => {
          const stepNum = i + 1;
          const isActive = step === stepNum;
          const isDone = step > stepNum;
          return (
            <React.Fragment key={`step-indicator-${stepNum}`}>
              <div className="flex flex-col items-center shrink-0">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-700 border-2 transition-all duration-200 ${
                    isDone
                      ? 'bg-gradient-primary border-primary text-white'
                      : isActive
                      ? 'border-primary text-primary bg-primary/10' :'border-border text-muted-foreground bg-muted/30'
                  }`}
                >
                  {isDone ? <Check size={13} /> : stepNum}
                </div>
                <span
                  className={`text-[10px] font-500 mt-1 ${
                    isActive ? 'text-primary' : isDone ? 'text-foreground' : 'text-muted-foreground'
                  }`}
                >
                  {label}
                </span>
              </div>
              {i < STEP_LABELS.length - 1 && (
                <div
                  className={`step-connector mb-4 mx-1 ${isDone ? 'step-connector-active' : ''}`}
                  aria-hidden="true"
                />
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Step 1: Account */}
      {step === 1 && (
        <form onSubmit={handleStep1} noValidate className="space-y-4">
          <div>
            <h2 className="text-xl font-700 text-foreground mb-0.5">Create your account</h2>
            <p className="text-muted-foreground text-sm">Start creating AI-powered ads in minutes</p>
          </div>

          {/* Google Sign Up */}
          <button
            type="button"
            onClick={handleGoogleSignUp}
            disabled={isGoogleLoading}
            className="w-full flex items-center justify-center gap-3 bg-muted/40 border border-border rounded-lg py-2.5 text-sm font-500 text-foreground hover:bg-muted/70 transition-all duration-150 active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {isGoogleLoading ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
              </svg>
            )}
            {isGoogleLoading ? 'Redirecting to Google...' : 'Continue with Google'}
          </button>

          {/* Divider */}
          <div className="flex items-center gap-3">
            <div className="flex-1 h-px bg-border" />
            <span className="text-xs text-muted-foreground">or sign up with email</span>
            <div className="flex-1 h-px bg-border" />
          </div>

          <div>
            <label htmlFor="signup-name" className="block text-sm font-500 text-foreground mb-1.5">Full Name</label>
            <input
              id="signup-name"
              type="text"
              placeholder="Rahul Sharma"
              className={`w-full bg-input border rounded-lg px-3.5 py-2.5 text-sm text-foreground placeholder:text-muted-foreground outline-none focus:ring-2 focus:ring-ring transition-all duration-150 ${s1.fullName ? 'border-red-500' : 'border-border focus:border-primary/50'}`}
              {...step1Form.register('fullName', { required: 'Full name is required', minLength: { value: 2, message: 'Name is too short' } })}
            />
            {s1.fullName && <p className="text-red-400 text-xs mt-1.5 flex items-center gap-1"><AlertCircle size={11} />{s1.fullName.message}</p>}
          </div>

          <div>
            <label htmlFor="signup-email" className="block text-sm font-500 text-foreground mb-1.5">Email Address</label>
            <input
              id="signup-email"
              type="email"
              placeholder="you@business.com"
              className={`w-full bg-input border rounded-lg px-3.5 py-2.5 text-sm text-foreground placeholder:text-muted-foreground outline-none focus:ring-2 focus:ring-ring transition-all duration-150 ${s1.email ? 'border-red-500' : 'border-border focus:border-primary/50'}`}
              {...step1Form.register('email', {
                required: 'Email is required',
                pattern: { value: /^\S+@\S+\.\S+$/, message: 'Enter a valid email address' },
              })}
            />
            {s1.email && <p className="text-red-400 text-xs mt-1.5 flex items-center gap-1"><AlertCircle size={11} />{s1.email.message}</p>}
          </div>

          <div>
            <label htmlFor="signup-password" className="block text-sm font-500 text-foreground mb-1.5">Password</label>
            <p className="text-xs text-muted-foreground mb-1.5">Minimum 8 characters with at least one number</p>
            <div className="relative">
              <input
                id="signup-password"
                type={showPassword ? 'text' : 'password'}
                placeholder="Create a strong password"
                className={`w-full bg-input border rounded-lg px-3.5 py-2.5 pr-10 text-sm text-foreground placeholder:text-muted-foreground outline-none focus:ring-2 focus:ring-ring transition-all duration-150 ${s1.password ? 'border-red-500' : 'border-border focus:border-primary/50'}`}
                {...step1Form.register('password', {
                  required: 'Password is required',
                  minLength: { value: 8, message: 'Password must be at least 8 characters' },
                  pattern: { value: /(?=.*\d)/, message: 'Password must contain at least one number' },
                })}
              />
              <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors" aria-label={showPassword ? 'Hide password' : 'Show password'}>
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            {s1.password && <p className="text-red-400 text-xs mt-1.5 flex items-center gap-1"><AlertCircle size={11} />{s1.password.message}</p>}
          </div>

          <div>
            <label htmlFor="signup-confirm" className="block text-sm font-500 text-foreground mb-1.5">Confirm Password</label>
            <div className="relative">
              <input
                id="signup-confirm"
                type={showConfirm ? 'text' : 'password'}
                placeholder="Re-enter your password"
                className={`w-full bg-input border rounded-lg px-3.5 py-2.5 pr-10 text-sm text-foreground placeholder:text-muted-foreground outline-none focus:ring-2 focus:ring-ring transition-all duration-150 ${s1.confirmPassword ? 'border-red-500' : 'border-border focus:border-primary/50'}`}
                {...step1Form.register('confirmPassword', {
                  required: 'Please confirm your password',
                  validate: (v) => v === step1Form.watch('password') || 'Passwords do not match',
                })}
              />
              <button type="button" onClick={() => setShowConfirm(!showConfirm)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors" aria-label={showConfirm ? 'Hide confirm password' : 'Show confirm password'}>
                {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            {s1.confirmPassword && <p className="text-red-400 text-xs mt-1.5 flex items-center gap-1"><AlertCircle size={11} />{s1.confirmPassword.message}</p>}
          </div>

          <button type="submit" className="w-full bg-gradient-primary text-white font-600 py-2.5 rounded-lg text-sm transition-all duration-150 hover:opacity-90 active:scale-[0.98] flex items-center justify-center gap-2">
            Continue <ChevronRight size={16} />
          </button>

          <p className="text-center text-xs text-muted-foreground">
            By creating an account you agree to our{' '}
            <span className="text-primary cursor-pointer hover:text-accent transition-colors">Terms of Service</span>
            {' '}and{' '}
            <span className="text-primary cursor-pointer hover:text-accent transition-colors">Privacy Policy</span>
          </p>

          <p className="text-center text-sm text-muted-foreground">
            Already have an account?{' '}
            <button type="button" onClick={onSwitchToLogin} className="text-primary hover:text-accent font-600 transition-colors">Sign in</button>
          </p>
        </form>
      )}

      {/* Step 2: Brand Setup */}
      {step === 2 && (
        <form onSubmit={handleStep2} noValidate className="space-y-4">
          <div>
            <h2 className="text-xl font-700 text-foreground mb-0.5">Set up your brand</h2>
            <p className="text-muted-foreground text-sm">This appears on every generated ad creative</p>
          </div>

          <div>
            <label htmlFor="biz-name" className="block text-sm font-500 text-foreground mb-1.5">Business Name</label>
            <p className="text-xs text-muted-foreground mb-1.5">This will appear at the TOP of every video ad</p>
            <input
              id="biz-name"
              type="text"
              placeholder="ABC FASHION"
              className={`w-full bg-input border rounded-lg px-3.5 py-2.5 text-sm text-foreground placeholder:text-muted-foreground outline-none focus:ring-2 focus:ring-ring transition-all duration-150 ${s2.businessName ? 'border-red-500' : 'border-border focus:border-primary/50'}`}
              {...step2Form.register('businessName', { required: 'Business name is required', minLength: { value: 2, message: 'Business name is too short' } })}
            />
            {s2.businessName && <p className="text-red-400 text-xs mt-1.5 flex items-center gap-1"><AlertCircle size={11} />{s2.businessName.message}</p>}
          </div>

          <div>
            <label htmlFor="phone1" className="block text-sm font-500 text-foreground mb-1.5">Mobile Number 1</label>
            <p className="text-xs text-muted-foreground mb-1.5">Appears at the BOTTOM of every video ad</p>
            <div className="flex">
              <div className="phone-input-prefix">🇮🇳 +91</div>
              <input
                id="phone1"
                type="tel"
                placeholder="98765 43210"
                className={`phone-input-field flex-1 bg-input border rounded-r-lg px-3.5 py-2.5 text-sm text-foreground placeholder:text-muted-foreground outline-none focus:ring-2 focus:ring-ring transition-all duration-150 ${s2.phone1 ? 'border-red-500' : 'border-border focus:border-primary/50'}`}
                {...step2Form.register('phone1', {
                  required: 'Mobile number 1 is required',
                  pattern: { value: INDIAN_PHONE_REGEX, message: 'Enter a valid Indian mobile number (e.g. 98765 43210)' },
                })}
              />
            </div>
            {s2.phone1 && <p className="text-red-400 text-xs mt-1.5 flex items-center gap-1"><AlertCircle size={11} />{s2.phone1.message}</p>}
          </div>

          <div>
            <label htmlFor="phone2" className="block text-sm font-500 text-foreground mb-1.5">Mobile Number 2 <span className="text-muted-foreground font-400">(optional)</span></label>
            <p className="text-xs text-muted-foreground mb-1.5">Also appears at the BOTTOM alongside Mobile Number 1</p>
            <div className="flex">
              <div className="phone-input-prefix">🇮🇳 +91</div>
              <input
                id="phone2"
                type="tel"
                placeholder="91234 56789"
                className={`phone-input-field flex-1 bg-input border rounded-r-lg px-3.5 py-2.5 text-sm text-foreground placeholder:text-muted-foreground outline-none focus:ring-2 focus:ring-ring transition-all duration-150 ${s2.phone2 ? 'border-red-500' : 'border-border focus:border-primary/50'}`}
                {...step2Form.register('phone2', {
                  pattern: { value: INDIAN_PHONE_REGEX, message: 'Enter a valid Indian mobile number' },
                })}
              />
            </div>
            {s2.phone2 && <p className="text-red-400 text-xs mt-1.5 flex items-center gap-1"><AlertCircle size={11} />{s2.phone2.message}</p>}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="website" className="block text-sm font-500 text-foreground mb-1.5">Website</label>
              <input
                id="website"
                type="url"
                placeholder="https://yourbrand.com"
                className="w-full bg-input border border-border rounded-lg px-3.5 py-2.5 text-sm text-foreground placeholder:text-muted-foreground outline-none focus:ring-2 focus:ring-ring focus:border-primary/50 transition-all duration-150"
                {...step2Form.register('website')}
              />
            </div>
            <div>
              <label htmlFor="biz-category" className="block text-sm font-500 text-foreground mb-1.5">Category</label>
              <select
                id="biz-category"
                className="w-full bg-input border border-border rounded-lg px-3.5 py-2.5 text-sm text-foreground outline-none focus:ring-2 focus:ring-ring focus:border-primary/50 transition-all duration-150"
                {...step2Form.register('businessCategory', { required: 'Select a category' })}
              >
                <option value="">Select...</option>
                {BUSINESS_CATEGORIES.map((cat) => (
                  <option key={`cat-${cat}`} value={cat}>{cat}</option>
                ))}
              </select>
              {s2.businessCategory && <p className="text-red-400 text-xs mt-1.5 flex items-center gap-1"><AlertCircle size={11} />{s2.businessCategory.message}</p>}
            </div>
          </div>

          <div className="flex gap-3 pt-1">
            <button type="button" onClick={() => setStep(1)} className="flex items-center gap-1.5 px-4 py-2.5 rounded-lg text-sm font-500 text-muted-foreground bg-muted/40 hover:bg-muted/70 border border-border transition-all duration-150 active:scale-[0.98]">
              <ChevronLeft size={16} /> Back
            </button>
            <button type="submit" className="flex-1 bg-gradient-primary text-white font-600 py-2.5 rounded-lg text-sm transition-all duration-150 hover:opacity-90 active:scale-[0.98] flex items-center justify-center gap-2">
              Continue <ChevronRight size={16} />
            </button>
          </div>
        </form>
      )}

      {/* Step 3: Style */}
      {step === 3 && (
        <form onSubmit={handleStep3} noValidate className="space-y-5">
          <div>
            <h2 className="text-xl font-700 text-foreground mb-0.5">Brand style</h2>
            <p className="text-muted-foreground text-sm">Customize how your brand looks on ads</p>
          </div>

          <div>
            <label htmlFor="brand-color" className="block text-sm font-500 text-foreground mb-1.5">Primary Brand Color</label>
            <p className="text-xs text-muted-foreground mb-2">Used for text backgrounds and overlays on your ads</p>
            <div className="flex items-center gap-3">
              <input
                id="brand-color"
                type="color"
                className="w-12 h-10 rounded-lg border border-border bg-input cursor-pointer p-1"
                {...step3Form.register('primaryColor')}
              />
              <div className="flex gap-2 flex-wrap">
                {['#7C3AED', '#DC2626', '#0891B2', '#059669', '#D97706', '#DB2777', '#1D4ED8'].map((color) => (
                  <button
                    type="button"
                    key={`color-${color}`}
                    onClick={() => step3Form.setValue('primaryColor', color)}
                    className="w-8 h-8 rounded-lg border-2 border-border hover:border-foreground transition-all duration-150 active:scale-90"
                    style={{ backgroundColor: color }}
                    aria-label={`Select color ${color}`}
                  />
                ))}
              </div>
            </div>
          </div>

          <div>
            <label htmlFor="default-lang" className="block text-sm font-500 text-foreground mb-1.5">Default Language for AI Copy</label>
            <select
              id="default-lang"
              className="w-full bg-input border border-border rounded-lg px-3.5 py-2.5 text-sm text-foreground outline-none focus:ring-2 focus:ring-ring focus:border-primary/50 transition-all duration-150"
              {...step3Form.register('defaultLanguage')}
            >
              {['English', 'Hindi', 'Hinglish', 'Tamil', 'Telugu', 'Kannada', 'Marathi', 'Bengali'].map((lang) => (
                <option key={`lang-${lang}`} value={lang}>{lang}</option>
              ))}
            </select>
          </div>

          <div className="flex gap-3 pt-1">
            <button type="button" onClick={() => setStep(2)} className="flex items-center gap-1.5 px-4 py-2.5 rounded-lg text-sm font-500 text-muted-foreground bg-muted/40 hover:bg-muted/70 border border-border transition-all duration-150 active:scale-[0.98]">
              <ChevronLeft size={16} /> Back
            </button>
            <button type="submit" className="flex-1 bg-gradient-primary text-white font-600 py-2.5 rounded-lg text-sm transition-all duration-150 hover:opacity-90 active:scale-[0.98] flex items-center justify-center gap-2">
              Continue <ChevronRight size={16} />
            </button>
          </div>
        </form>
      )}

      {/* Step 4: Plan Selection */}
      {step === 4 && (
        <div className="space-y-5">
          <div>
            <h2 className="text-xl font-700 text-foreground mb-0.5">Choose your plan</h2>
            <p className="text-muted-foreground text-sm">Start free, upgrade anytime. No credit card required for Free.</p>
          </div>

          <div className="grid grid-cols-2 gap-2.5">
            {PLANS.map((plan) => (
              <button
                type="button"
                key={`plan-${plan.id}`}
                onClick={() => setSelectedPlan(plan.id)}
                className={`relative text-left p-3 rounded-xl border-2 transition-all duration-150 ${
                  selectedPlan === plan.id
                    ? 'border-primary bg-primary/10' :'border-border bg-muted/20 hover:border-primary/40 hover:bg-muted/40'
                }`}
              >
                {plan.highlight && (
                  <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-gradient-primary text-white text-[10px] font-700 px-2.5 py-0.5 rounded-full whitespace-nowrap">
                    RECOMMENDED
                  </span>
                )}
                <p className="text-xs font-700 text-muted-foreground mb-1">{plan.name}</p>
                <p className="text-lg font-800 text-foreground tabular-nums">{plan.price}</p>
                <p className="text-[11px] text-muted-foreground mt-1">{plan.creatives}</p>
                {selectedPlan === plan.id && (
                  <div className="absolute top-2.5 right-2.5 w-4 h-4 rounded-full bg-gradient-primary flex items-center justify-center">
                    <Check size={10} className="text-white" />
                  </div>
                )}
              </button>
            ))}
          </div>

          <div className="flex gap-3">
            <button type="button" onClick={() => setStep(3)} className="flex items-center gap-1.5 px-4 py-2.5 rounded-lg text-sm font-500 text-muted-foreground bg-muted/40 hover:bg-muted/70 border border-border transition-all duration-150 active:scale-[0.98]">
              <ChevronLeft size={16} /> Back
            </button>
            <button
              type="button"
              onClick={handleFinalSubmit}
              disabled={isLoading}
              className="flex-1 bg-gradient-primary text-white font-600 py-2.5 rounded-lg text-sm transition-all duration-150 hover:opacity-90 active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <><Loader2 size={15} className="animate-spin" /> Creating your workspace...</>
              ) : (
                <><Sparkles size={15} /> Start Creating Ads</>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}